import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from '../../components/AdminLayout';
import { 
  Mail, 
  MailOpen, 
  Trash2, 
  Clock, 
  User, 
  Phone,
  ChevronDown,
  ChevronUp,
  Inbox,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const { getAuthHeader } = useAdminAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch messages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/messages/${id}/read`, {}, { headers: getAuthHeader() });
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
      toast({ title: 'Success', description: 'Message marked as read' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update message', variant: 'destructive' });
    }
  };

  const handleMarkAsUnread = async (id) => {
    try {
      await axios.put(`${API_URL}/api/messages/${id}/unread`, {}, { headers: getAuthHeader() });
      setMessages(messages.map(m => m.id === id ? { ...m, read: false } : m));
      toast({ title: 'Success', description: 'Message marked as unread' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update message', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/messages/${id}`, { headers: getAuthHeader() });
      setMessages(messages.filter(m => m.id !== id));
      toast({ title: 'Success', description: 'Message deleted' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete message', variant: 'destructive' });
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      // Mark as read when expanded
      const message = messages.find(m => m.id === id);
      if (message && !message.read) {
        handleMarkAsRead(id);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'read') return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All messages read'}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All ({messages.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === 'unread'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === 'read'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Read ({messages.length - unreadCount})
          </button>
        </div>

        {/* Messages List */}
        {filteredMessages.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No messages</h3>
            <p className="text-gray-500">
              {filter === 'unread' ? 'No unread messages' : 'No messages yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`bg-white rounded-lg border transition-all ${
                  message.read ? 'border-gray-200' : 'border-blue-300 bg-blue-50/30'
                }`}
              >
                {/* Message Header */}
                <div
                  onClick={() => toggleExpand(message.id)}
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      message.read ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      {message.read ? (
                        <MailOpen className="w-5 h-5 text-gray-500" />
                      ) : (
                        <Mail className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className={`text-sm truncate ${message.read ? 'font-medium text-gray-700' : 'font-semibold text-gray-900'}`}>
                          {message.name}
                        </h3>
                        {!message.read && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className={`text-sm truncate ${message.read ? 'text-gray-500' : 'text-gray-700'}`}>
                        {message.subject}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatDate(message.created_at)}
                    </span>
                    {expandedId === message.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === message.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4 space-y-4">
                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${message.email}`} className="text-blue-600 hover:underline">
                            {message.email}
                          </a>
                        </div>
                        {message.phone && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${message.phone}`} className="text-blue-600 hover:underline">
                              {message.phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center space-x-2 text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(message.created_at)}</span>
                        </div>
                      </div>

                      {/* Message Content */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-3 pt-2">
                        {message.read ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkAsUnread(message.id); }}
                            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            <span>Mark as Unread</span>
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkAsRead(message.id); }}
                            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                            <span>Mark as Read</span>
                          </button>
                        )}
                        <a
                          href={`mailto:${message.email}?subject=Re: ${message.subject}`}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          <span>Reply via Email</span>
                        </a>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(message.id); }}
                          className="flex items-center space-x-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
